/**
 * Where the POSH quiz keeps an attempt in progress (its shuffle seed and
 * the answers so far) — sessionStorage, so a reload keeps both.
 *
 * The key carries the tenant AND the person: on a shared device (a shop
 * counter, a factory kiosk) the next person to sign in must never see —
 * or submit — the previous person's answers. Logout also clears every
 * key with this prefix (auth-context.tsx → clearPoshQuizStorage).
 *
 * Plain .ts with no imports so `npm run test:unit` can load it.
 */

export const POSH_QUIZ_STORAGE_PREFIX = "wr_posh_quiz_v1:";

export function quizStorageKey(
  tenantSlug: string,
  userId: number | string | null | undefined,
  moduleId: number,
): string {
  const who = userId === null || userId === undefined || userId === "" ? "anon" : String(userId);
  return `${POSH_QUIZ_STORAGE_PREFIX}${tenantSlug}:${who}:${moduleId}`;
}

/** The parts of Storage this needs (sessionStorage, or a test double). */
export interface KeyedStorage {
  readonly length: number;
  key(index: number): string | null;
  removeItem(key: string): void;
}

/**
 * Removes every saved quiz attempt. Never throws — storage can be
 * blocked (private window, site data off) and logout must still finish.
 * Returns how many keys were removed.
 */
export function clearPoshQuizStorage(storage?: KeyedStorage | null): number {
  try {
    const s =
      storage ?? (typeof window !== "undefined" ? window.sessionStorage : null);
    if (!s) return 0;
    const doomed: string[] = [];
    for (let i = 0; i < s.length; i++) {
      const k = s.key(i);
      if (k && k.startsWith(POSH_QUIZ_STORAGE_PREFIX)) doomed.push(k);
    }
    for (const k of doomed) s.removeItem(k);
    return doomed.length;
  } catch {
    return 0;
  }
}
